import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";

const inputCls = "w-full bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 focus:border-brand-red outline-none";
const labelCls = "text-[10px] font-mono uppercase tracking-widest text-zinc-500";

export default function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`Staff login created — share the username "${form.username}" and password with them`);
      setForm({ name: "", username: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create staff account");
    } finally { setBusy(false); }
  };

  const revoke = async (s) => {
    if (!window.confirm(`Revoke ${s.name}'s dashboard access? They will be logged out immediately.`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast.success(`${s.name}'s access revoked`);
      load();
    } catch { toast.error("Couldn't revoke access"); }
  };

  return (
    <div data-testid="staff-manager">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck className="h-4 w-4 text-brand-red" />
        <h3 className="font-display uppercase text-xl text-white tracking-tight">Staff dashboard logins</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Each staff member gets their own username and password for this dashboard. Revoking takes effect immediately. Staff can do everything except manage staff accounts.</p>

      <form onSubmit={create} data-testid="staff-create-form" className="border border-zinc-900 bg-zinc-950 p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 max-w-3xl">
        <div><span className={labelCls}>Full name</span><input data-testid="staff-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dave Smith" /></div>
        <div><span className={labelCls}>Username</span><input data-testid="staff-username" required minLength={3} className={inputCls} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} placeholder="dave" /></div>
        <div><span className={labelCls}>Password (min 8)</span><input data-testid="staff-password" required minLength={8} type="text" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Strong password" /></div>
        <div className="flex items-end">
          <button data-testid="staff-create-btn" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-red text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
            <UserPlus className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create login"}
          </button>
        </div>
      </form>

      <div className="mt-5 space-y-px bg-zinc-900 border border-zinc-900 max-w-3xl" data-testid="staff-list">
        {staff.length === 0 && <div className="bg-zinc-950 p-6 text-center text-sm text-zinc-500">No staff logins yet.</div>}
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.id}`} className="bg-zinc-950 p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-sm text-white font-semibold">{s.name}</div>
              <div className="text-xs text-zinc-500 font-mono">username: {s.username} · added {(s.created_at || "").slice(0, 10)}</div>
            </div>
            <button data-testid={`staff-revoke-${s.id}`} onClick={() => revoke(s)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 text-[11px] font-mono uppercase tracking-widest text-zinc-300 hover:border-brand-red hover:text-brand-red">
              <Trash2 className="h-3 w-3" /> Revoke access
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
